import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  SimpleChanges,
  viewChild
} from '@angular/core';
import { ReplaySubject, fromEvent } from 'rxjs';
import { debounceTime, map } from 'rxjs/operators';

import { EventTypes } from 'src/app/core/constants/event-types';
import { LoggerService } from 'src/app/core/services/logger.service';
import { CustomMenuDto } from '../../models/custom-menu-dto';
import { CustomClaimDto } from './../../models/custom-claim-dto';
import { BaseIndexModule } from 'src/app/pages/shared/base/modules/base-index.module';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';

@Component({
    selector: 'app-panel-menus',
    templateUrl: './panel-menus.component.html',
    styleUrls: ['./panel-menus.component.scss'],
    imports: [BaseIndexModule, ReactiveFormsModule, FormsModule]
})
export class PanelMenusComponent implements OnInit {
  //#region properties

  tagName: string;

  readonly searchInput = viewChild<ElementRef>('searchInput');

  @Input('menusClaims') menusClaims: Array<CustomMenuDto>;
  @Input('isChangeStatus') isChangeStatus: number = 0;
  @Input('isEditMode') isEditMode: boolean = false;
  @Input('resetFormCounter') resetFormCounter: number = 0;

  @Output('claims') output_claims = new EventEmitter<CustomClaimDto[]>();
  @Output('onChangeMenuClaimsStatus') output_changeStatus = new EventEmitter<
    Array<CustomClaimDto>
  >();
  @Output('menuTitleSelected') output_menuTitleSelected =
    new EventEmitter<string>();

  filteredMenusClaims$: ReplaySubject<CustomMenuDto[]> = new ReplaySubject<
    CustomMenuDto[]
  >(1);

  claims: CustomClaimDto[];
  isSelectAll: boolean;

  menuTitleSelected: string;

  checkBoxMenuElements: NodeListOf<Element>;
  menuElements: NodeListOf<Element>;

  searchedText: string;

  menuSelected: number = 0;
  menusCount: number = 0;
  claimsCount: number = 0;
  claimsSelectedCount: number = 0;

  baseMenuClaims: Array<CustomMenuDto>;

  //#endregion

  constructor(
    private loggerService: LoggerService,
    protected elementRef: ElementRef
  ) {
    this.tagName = elementRef.nativeElement.tagName.toLowerCase();
  }

  ngOnInit() {
    try {
      this.baseMenuClaims = JSON.parse(JSON.stringify(this.menusClaims));

      this.filteredMenusClaims$.subscribe((res) => {
        this.getClaimsCount();
        this.claimsSelectedCount = this.getSelectedClaimsCount(
          this.menusClaims
        );

        //role title active
        this.menuTitleSelected = res[0]?.title;
        this.emitMenuTitle();

        //role claims active
        this.claims = res[0]?.claims;
        this.emitClaims();
      });
    } catch (error) {
      this.loggerService.logError(
        this.tagName,
        error,
        EventTypes.systemManagement
      );
    }
  }

  ngAfterViewInit(): void {
    this.checkBoxMenuElements = this.getAllCheckBoxElements();
    this.menuElements = this.getAllMenuElements();

    this.searchInputListener();
    this.filteredMenusClaims$.next(this.menusClaims);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isChangeStatus'] && this.isChangeStatus)
      this.onChangeClaimsMenusStatus();

    if (changes['resetFormCounter'] && this.resetFormCounter > 0)
      this.onReset();
  }

  getClaims(claims: CustomClaimDto[]) {
    this.claims = claims;
    this.emitClaims();
    this.emitMenuTitle();
  }

  //#region get elements

  getAllMenuElements() {
    try {
      const menuElements = document.querySelectorAll(
        `${this.tagName} [data-id]`
      );
      return menuElements;
    } catch (error) {
      this.loggerService.logError(
        this.tagName,
        error,
        EventTypes.systemManagement
      );
    }
  }

  getAllCheckBoxElements(): NodeListOf<Element> {
    try {
      const checkBoxes = document.querySelectorAll(
        `${this.tagName} [data-checkbox-id]`
      );
      return checkBoxes;
    } catch (error) {
      this.loggerService.logError(
        this.tagName,
        error,
        EventTypes.systemManagement
      );
    }
  }

  getCheckBoxByMenuId(menuId: number): Element {
    try {
      // const checkBoxes = this.checkBoxMenuElements;
      let element;
      this.checkBoxMenuElements.forEach((el) => {
        const DataAttr = el.getAttribute('data-checkbox-id');
        if (+DataAttr == menuId) element = el;
      });

      return element;
    } catch (error) {
      this.loggerService.logError(
        this.tagName,
        error,
        EventTypes.systemManagement
      );
    }
  }

  getCheckBoxesByMenuIdList(menuIds: number[]): Element[] {
    try {
      let checkBoxes: Element[] = [];
      menuIds.forEach((menuId) => {
        this.checkBoxMenuElements.forEach((el) => {
          const DataAttr = el.getAttribute('data-checkbox-id');
          if (+DataAttr == menuId) checkBoxes.push(el);
        });
      });

      return checkBoxes;
    } catch (error) {
      this.loggerService.logError(
        this.tagName,
        error,
        EventTypes.systemManagement
      );
    }
  }

  //#endregion

  //#region change status when change claims

  onChangeClaimsMenusStatus() {
    try {
      const onChangeMenuStatus = (array: CustomMenuDto[]) =>
        array.forEach((menu) => {
          if (menu.claims.length) {
            if (
              menu.claims
                .filter((item) => item.isVisible)
                .every((item) => {
                  return item.selected == true;
                })
            )
              this.onSelectMenuCheckBox(menu.id);
            else if (
              menu.claims
                .filter((item) => item.isVisible)
                .some((item) => {
                  return item.selected == true;
                })
            )
              this.onHighlightMenuCheckBox(menu.id);
            else this.onUnSelectMenuCheckBox(menu.id);
          }

          if (menu.subMenus.length) {
            onChangeMenuStatus(menu.subMenus);
            this.isSelectedAllSubMenu(
              menu.id,
              menu.subMenus.map((menu) => {
                return menu.id;
              })
            );
          }
        });

      if (this.menusClaims?.length) onChangeMenuStatus(this.menusClaims);

      this.isSelectAllMenus();
      this.claimsSelectedCount = this.getSelectedClaimsCount(this.menusClaims);
    } catch (error) {
      this.loggerService.logError(
        this.tagName,
        error,
        EventTypes.systemManagement
      );
    }

    this.isSelectAllMenus();
    this.claimsSelectedCount = this.getSelectedClaimsCount(this.menusClaims);
  }

  onSelectMenuCheckBox(menuId) {
    try {
      let checkboxMenu = this.getCheckBoxByMenuId(menuId);

      if (!checkboxMenu) return;

      if (checkboxMenu.classList.contains('bg-full'))
        checkboxMenu.classList.remove('bg-full');

      (checkboxMenu as HTMLInputElement).checked = true;
    } catch (error) {
      this.loggerService.logError(
        this.tagName,
        error,
        EventTypes.systemManagement
      );
    }
  }

  onHighlightMenuCheckBox(menuId) {
    try {
      let checkboxMenu = this.getCheckBoxByMenuId(menuId);

      if (!checkboxMenu) return;

      (checkboxMenu as HTMLInputElement).checked = false;

      if (!checkboxMenu.classList.contains('bg-full'))
        checkboxMenu.classList.add('bg-full');
    } catch (error) {
      this.loggerService.logError(
        this.tagName,
        error,
        EventTypes.systemManagement
      );
    }
  }

  onUnSelectMenuCheckBox(menuId) {
    try {
      let checkboxMenu = this.getCheckBoxByMenuId(menuId);

      if (!checkboxMenu) return;

      if (checkboxMenu.classList.contains('bg-full'))
        checkboxMenu.classList.remove('bg-full');
      (checkboxMenu as HTMLInputElement).checked = false;
    } catch (error) {
      this.loggerService.logError(
        this.tagName,
        error,
        EventTypes.systemManagement
      );
    }
  }

  isSelectedAllSubMenu(menuId, menuIds: number[]) {
    try {
      let checkBoxes: Element[] = this.getCheckBoxesByMenuIdList(menuIds);

      if (!checkBoxes?.length) return;

      if (
        checkBoxes.every((checkBox) => {
          return (checkBox as HTMLInputElement).checked == true;
        })
      )
        this.onSelectMenuCheckBox(menuId);
      else if (
        checkBoxes.some((checkBox) => {
          return (checkBox as HTMLInputElement).checked == true;
        })
      )
        this.onHighlightMenuCheckBox(menuId);
      else if (
        checkBoxes.some((checkBox) => {
          return (checkBox as HTMLInputElement).classList.contains('bg-full');
        })
      )
        this.onHighlightMenuCheckBox(menuId);
      else this.onUnSelectMenuCheckBox(menuId);
    } catch (error) {
      this.loggerService.logError(
        this.tagName,
        error,
        EventTypes.systemManagement
      );
    }
  }

  //#endregion

  //#region select

  onSelect(event, menu: CustomMenuDto) {
    try {
      let checked;

      if (event) checked = event?.target?.checked;

      const onSelectMenus = (menu: CustomMenuDto) => {
        //select claims
        this.onSelectAllMenuClaims(menu, checked);

        //change menu checkbox status
        if (checked) this.onSelectMenuCheckBox(menu.id);
        else this.onUnSelectMenuCheckBox(menu.id);

        // select children
        if (menu.subMenus.length)
          menu.subMenus.forEach((subMenu) => {
            if (subMenu.isVisible) onSelectMenus(subMenu);
          });
      };

      onSelectMenus(menu);

      this.onChangeClaimsMenusStatus();
    } catch (error) {
      this.loggerService.logError(
        this.tagName,
        error,
        EventTypes.systemManagement
      );
    }
  }

  onSelectAll(event) {
    try {
      let isSelectAll = event?.target?.checked;

      this.checkBoxMenuElements = this.getAllCheckBoxElements();

      this.checkBoxMenuElements?.forEach((checkBox) => {
        const isChecked = (checkBox as HTMLInputElement).checked;

        if (!isChecked && isSelectAll) (checkBox as HTMLInputElement).click();
        else if (isChecked && !isSelectAll)
          (checkBox as HTMLInputElement).click();
      });
    } catch (error) {
      this.loggerService.logError(
        this.tagName,
        error,
        EventTypes.systemManagement
      );
    }
  }

  onSelectAllMenuClaims(menu: CustomMenuDto, isSelect: boolean) {
    try {
      if (!menu?.claims?.length) return;

      menu.claims.map((claim) => {
        if (claim.isVisible) return (claim.selected = isSelect);
      });

      this.onChangeClaimsStatus(menu.claims);
    } catch (error) {
      this.loggerService.logError(
        this.tagName,
        error,
        EventTypes.systemManagement
      );
    }
  }

  onChangeClaimsStatus(claims: CustomClaimDto[]) {
    try {
      const TimeOut = setTimeout(() => {
        this.output_changeStatus.emit(claims);
        clearTimeout(TimeOut);
      }, 1);
    } catch (error) {
      this.loggerService.logError(
        this.tagName,
        error,
        EventTypes.systemManagement
      );
    }
  }

  isSelectAllMenus() {
    try {
      this.isSelectAll = true;
      this.checkBoxMenuElements = this.getAllCheckBoxElements();
      if (!this.checkBoxMenuElements.length) this.isSelectAll = false;
      this.checkBoxMenuElements.forEach((el) => {
        if ((el as HTMLInputElement).checked == false) {
          this.isSelectAll = false;
          return;
        }
      });
    } catch (error) {
      this.loggerService.logError(
        this.tagName,
        error,
        EventTypes.systemManagement
      );
    }
  }

  //#endregion

  //#region other

  onActiveMenu(menuId, menuTitle) {
    try {
      this.menuSelected = menuId;
      this.menuTitleSelected = menuTitle;
      this.emitMenuTitle();

      //remove old active element
      const treeElements = document.querySelectorAll(
        `${this.tagName} [data-id]`
      );
      treeElements.forEach((element) => {
        if (element.classList.contains('row-active')) {
          element.classList.remove('row-active');
        }
      });

      //active element now
      let treeItem = document.getElementById('menu-' + menuId);
      treeItem.classList.add('row-active');
    } catch (error) {
      this.loggerService.logError(
        this.tagName,
        error,
        EventTypes.systemManagement
      );
    }
  }

  onExpandAll() {
    try {
      const toggleMenuBtns = document.querySelectorAll(
        ` ${this.tagName} .toggle-menu-btn`
      );
      toggleMenuBtns.forEach((toggleMenuBtn) => {
        if (!toggleMenuBtn.classList.contains('mdi-rotate-90'))
          toggleMenuBtn.classList.add('mdi-rotate-90');
      });
      this.menuElements.forEach((menuElement) => {
        if (menuElement.classList.contains('d-none'))
          menuElement.classList.remove('d-none');
      });
    } catch (error) {
      this.loggerService.logError(
        this.tagName,
        error,
        EventTypes.systemManagement
      );
    }
  }

  onCollapseAll() {
    try {
      const toggleMenuBtns = document.querySelectorAll(
        ` ${this.tagName} .toggle-menu-btn`
      );
      toggleMenuBtns.forEach((toggleMenuBtn) => {
        if (toggleMenuBtn.classList.contains('mdi-rotate-90')) {
          toggleMenuBtn.classList.remove('mdi-rotate-90');
          toggleMenuBtn.classList.remove('text-danger');
        }
      });
      this.menuElements.forEach((menuElement) => {
        const isParentMenu = menuElement.classList.contains('parent-menu');
        if (!menuElement.classList.contains('d-none') && !isParentMenu)
          menuElement.classList.add('d-none');
      });
    } catch (error) {
      this.loggerService.logError(
        this.tagName,
        error,
        EventTypes.systemManagement
      );
    }
  }

  showSubMenusToggle(toggleIconElement, parentId) {
    try {
      const TimeOut = setTimeout(() => {
        this.checkBoxMenuElements = this.getAllCheckBoxElements();
        this.menuElements = this.getAllMenuElements();

        toggleIconElement.classList.toggle('mdi-rotate-90');
        toggleIconElement.classList.toggle('text-danger');

        //show Children
        this.menuElements.forEach((element) => {
          if (element.getAttribute('data-parentid') == parentId)
            element.classList.toggle('d-none');
        });

        //#region اگر والد بسته شد تمامی فرزندان بسته شوند
        if (toggleIconElement.classList.contains('mdi-rotate-90')) return;

        this.menuElements.forEach((child) => {
          const ParentId = child.getAttribute('data-parentid');

          //#region close all children by parent
          this.menuElements.forEach((parent) => {
            if (parent.getAttribute('data-id') == ParentId) {
              //اگر والد بسته بود فرزندم بسته شود
              if (parent.classList.contains('d-none')) {
                child.classList.add('d-none');
                return;
              }
            }
          });
          //#endregion

          //#region change style tree btn
          //اگر ایتمی بسته بود ایکونشم تغییر کنه
          if (child.classList.contains('d-none')) {
            let treeBtn = child.querySelector('.toggle-menu-btn');
            if (!treeBtn) return;
            treeBtn?.classList?.remove('mdi-rotate-90');
            treeBtn?.classList?.remove('text-danger');
          }
          //#endregion
        });

        //#endregion

        clearTimeout(TimeOut);
      }, 200);
    } catch (error) {
      this.loggerService.logError(
        this.tagName,
        error,
        EventTypes.systemManagement
      );
    }
  }

  onReset() {
    try {
      this.checkBoxMenuElements = this.getAllCheckBoxElements();

      this.checkBoxMenuElements?.forEach((checkBox) => {
        const isChecked = (checkBox as HTMLInputElement).checked;
        if (isChecked) (checkBox as HTMLInputElement).click();
      });

      if (this.isEditMode)
        this.menusClaims = JSON.parse(JSON.stringify(this.baseMenuClaims));

      this.ngOnInit();
      this.ngAfterViewInit();
    } catch (error) {
      this.loggerService.logError(
        this.tagName,
        error,
        EventTypes.systemManagement
      );
    }
  }

  //#endregion

  //#region search

  searchInputListener() {
    try {
      fromEvent(this.searchInput()?.nativeElement, 'keyup')
        .pipe(
          debounceTime(200),
          map((event) => event as KeyboardEvent),
          map((event) => (event.target as HTMLInputElement).value),
          map((text) => this.onSearch(text))
        )
        .subscribe((menusClaims) => {
          this.filteredMenusClaims$.next(menusClaims);
        });
    } catch (error) {
      this.loggerService.logError(
        this.tagName,
        error,
        EventTypes.systemManagement
      );
    }
  }

  onSearch(searchText: string): CustomMenuDto[] {
    try {
      this.refreshMenus();

      if (!searchText.trim()) return this.menusClaims;

      searchText = searchText.toLowerCase();

      const nestedSearch = (menus: CustomMenuDto[]) =>
        menus.forEach((menu) => {
          if (menu.title.toLowerCase().indexOf(searchText) > -1)
            menu.isVisible = true;
          else {
            //call nested method
            if (menu.subMenus?.length) nestedSearch(menu.subMenus);

            //filter claims
            if (menu.claims.length)
              menu.claims.forEach((claim) => {
                if (claim.title?.toLowerCase().indexOf(searchText) > -1)
                  claim.isVisible = true;
                else claim.isVisible = false;
              });

            //filter menus
            if (
              menu.subMenus.some((subMenu) => subMenu.isVisible) ||
              menu.claims.some((claim) => claim.isVisible)
            )
              menu.isVisible = true;
            else menu.isVisible = false;
          }
        });

      if (this.menusClaims?.length) nestedSearch(this.menusClaims);

      return this.menusClaims;
    } catch (error) {
      this.loggerService.logError(
        this.tagName,
        error,
        EventTypes.systemManagement
      );
    }
  }

  clearSearch() {
    this.refreshMenus();
    this.searchedText = '';
  }

  refreshMenus() {
    try {
      const visibleMenuClaims = (menus: CustomMenuDto[]) =>
        menus.forEach((menu) => {
          menu.isVisible = true;

          if (menu.claims?.length)
            menu.claims.map((claim) => (claim.isVisible = true));

          if (menu.subMenus?.length) visibleMenuClaims(menu.subMenus);
        });

      visibleMenuClaims(this.menusClaims);

      const TimeOut = setTimeout(() => {
        this.checkBoxMenuElements = this.getAllCheckBoxElements();
        this.menuElements = this.getAllMenuElements();
        if (this.menuElements?.length)
          (
            this.menuElements[0].querySelector('.menu-title') as HTMLElement
          )?.click();
        this.onChangeClaimsMenusStatus();
        this.onCollapseAll();
        this.getClaimsCount();
        this.claimsSelectedCount = this.getSelectedClaimsCount(
          this.menusClaims
        );
        clearTimeout(TimeOut);
      }, 200);
    } catch (error) {
      this.loggerService.logError(
        this.tagName,
        error,
        EventTypes.systemManagement
      );
    }
  }

  isVisibleMenu(): boolean {
    try {
      return this.menusClaims.some((menu) => menu.isVisible);
    } catch (error) {
      this.loggerService.logError(
        this.tagName,
        error,
        EventTypes.systemManagement
      );
    }
  }

  //#endregion

  //#region count

  getClaimsCount() {
    try {
      this.claimsCount = 0;
      let allClaims: number[] = [];

      const getAllClaimsCount = (menus: CustomMenuDto[]) =>
        menus.forEach((menu) => {
          if (menu.claims?.length) {
            const claimIds = menu.claims
              .filter((claim) => claim.isVisible)
              .map((claim) => claim.id);
            allClaims = allClaims.concat(claimIds);
          }

          if (menu.subMenus?.length) getAllClaimsCount(menu.subMenus);
        });

      getAllClaimsCount(this.menusClaims);

      this.claimsCount += [...new Set(allClaims)].length;
    } catch (error) {
      this.loggerService.logError(
        this.tagName,
        error,
        EventTypes.systemManagement
      );
    }
  }

  getSelectedClaimsCount(menuClaims: Array<CustomMenuDto>): number {
    try {
      let claimsSelectedList: number[] = [];

      const calcSelectedClaimsCount = (menus: CustomMenuDto[]) =>
        menus.forEach((item) => {
          if (item.claims.length)
            item.claims.forEach((claim) => {
              const isExist = claimsSelectedList.includes(claim.id);
              if (claim.selected == true && !isExist)
                claimsSelectedList.push(claim.id);
            });
          if (item.subMenus.length) calcSelectedClaimsCount(item.subMenus);
        });

      calcSelectedClaimsCount(menuClaims);
      return claimsSelectedList.length;
    } catch (error) {
      this.loggerService.logError(
        this.tagName,
        error,
        EventTypes.systemManagement
      );
    }
  }

  //#endregion

  //#region emit

  emitClaims() {
    this.output_claims.emit(this.claims);
  }

  emitMenuTitle() {
    this.output_menuTitleSelected.emit(this.menuTitleSelected);
  }

  //#endregion
}
